import {ColumnAlign, WjGridUtils} from './wjgrid-utils';
import {CellRangeEventArgs, Column, FlexGrid, FormatItemEventArgs} from '@mescius/wijmo.grid';
import {Globalize} from '@mescius/wijmo';
import {Signal, WritableSignal} from '@angular/core';

export interface ExcelCellData {
    format?: string;
    align?: ColumnAlign;
    cssClass?: string;
    isReadOnly?: boolean;
    valueSignal?: Signal<any> | WritableSignal<any>;
}

export interface ExcelRowFormat {
    align?: ColumnAlign;
    cssClass?: string;
    height?: number;
    isReadOnly?: boolean;
}

export type ExcelRow = { [key: string]:  string | number | boolean | Date | null | ExcelCellData };

const CellAlignmentClasses: { [key in ColumnAlign]: string } = {
    left: 'wj-align-left',
    center: 'wj-align-center',
    right: 'wj-align-right'
}

export const ExcelFmtReadonly: ExcelCellData = {
    isReadOnly: true
};

export const ExcelFmtEditable: ExcelCellData = {
    isReadOnly: false,
    cssClass: 'editable'
};

export const ExcelFmtEditableNotAllowed: ExcelCellData = {
    isReadOnly: true,
    cssClass: 'editable-not-allowed'
};

export const ExcelFmtReadonlyDotted: ExcelCellData = {
    isReadOnly: true,
    cssClass: 'polka-dot'
};


/**
 * The `WjGridCustomCells` class provides utility methods for enabling and managing custom cell formatting
 * features in a `FlexGrid`. It applies customized formatting, handles read-only settings, and integrates
 * cells with external data signals or formats for advanced behavior.
 *
 * This allows a Wijmo grid to behave in some way like an Excel table, where individual cells may
 * be formatted differently based on their content, or the row or column may be formatted differently.
 */
export class WjGridCustomCells {

    /**
     * Enables custom cell formatting for the specified FlexGrid by attaching necessary event handlers.
     */
    static enableCustomCellFormat(grid: FlexGrid) {
        grid.formatItem.addHandler(WjGridCustomCells.accountCellsFormatHandler);
        grid.beginningEdit.addHandler(WjGridCustomCells.beginEditHandler);
        grid.cellEditEnded.addHandler(WjGridCustomCells.endEditHandler);
    }

    static addColumnHeaderTooltips(grid: FlexGrid, tooltips: { [binding: string]: string }) {
        grid.formatItem.addHandler((s, e) => {
            if (e.panel === s.columnHeaders) {
                const column = s.columns[e.col];
                const tooltipText = tooltips[column.binding as string];
                if (tooltipText) {
                    const cell = e.cell;
                    cell.title = tooltipText;
                }
            }
        });
    }

    static setColumnFormat(column: Column, fmt: ExcelCellData) {
        (column as any).columnFmt = fmt;
    }

    static getColumnFormat(column: Column): ExcelCellData | undefined {
        return (column as any).columnFmt;
    }

    private static beginEditHandler(s: FlexGrid, e: CellRangeEventArgs) {
        const item = s.collectionView.items[e.row];
        const column = s.columns[e.col];

        const cellFmt = item[column.binding + 'CellFmt'] as ExcelCellData;
        const rowFmt = item['rowFmt'] as ExcelCellData;
        const colFmt = WjGridCustomCells.getColumnFormat(column);

        const isExplicitlyReadOnlyFalse = cellFmt?.isReadOnly === false || rowFmt?.isReadOnly === false || colFmt?.isReadOnly === false;

        //if (cellFmt?.isReadOnly ?? rowFmt?.isReadOnly ?? colFmt?.isReadOnly === true) {
        if (!isExplicitlyReadOnlyFalse) {
            e.cancel = true;
        }
    }

    private static endEditHandler(s: FlexGrid, e: CellRangeEventArgs) {
        const item = s.collectionView.items[e.row];
        const column = s.columns[e.col];
        const cellFmt = item[column.binding + 'CellFmt'] as ExcelCellData;
        const writableSignal = cellFmt?.valueSignal  as WritableSignal<any>;
        const isWritableSignal = writableSignal != null && typeof (writableSignal.set) === 'function';
        if (isWritableSignal) {
            const value = item[column.binding!];
            writableSignal.set.call(undefined, value);
        }

        WjGridCustomCells.refreshGridDataSignalValues(s);
    }

    private static refreshGridDataSignalValues(grid: FlexGrid) {
        const gridData = grid.collectionView.sourceCollection;
        const bindings = grid.columns.map(col => col.binding).filter(b => b != null) as string[];
        gridData.forEach(item => {
            bindings.forEach(binding => {
                const cellFmt = item[binding + 'CellFmt'] as ExcelCellData;
                const isExcelLikeCell = typeof cellFmt === 'object';

                if (isExcelLikeCell) {
                    const valueSignal = cellFmt.valueSignal;
                    if (typeof (valueSignal) === 'function') {
                        item[binding] = valueSignal.call(undefined);
                    }
                }
            });
        });
    }

    private static accountCellsFormatHandler(s: FlexGrid, e: FormatItemEventArgs) {

        if (e.panel === s.rowHeaders) {
        } else if (e.panel === s.cells) {
            const cell = e.cell;
            const item = s.collectionView.items[e.row];
            const column = s.columns[e.col];

            const cellFmt = item[column.binding + 'CellFmt'] as ExcelCellData;
            const isExcelLikeCell = typeof cellFmt === 'object';
            const isInputElement = WjGridUtils.hasAnyInputChildElement(cell);

            const rowFmt = item['rowFmt'] as ExcelRowFormat;
            if (typeof rowFmt === 'object') {
                const row = s.rows[e.row];
                if (rowFmt.align) {
                    cell.classList.toggle(CellAlignmentClasses[rowFmt.align], true);
                }

                if (rowFmt.cssClass) {
                    cell.classList.add(rowFmt.cssClass);
                }

                if (rowFmt.isReadOnly) {
                    cell.classList.add('readonly');
                }

                if (rowFmt.height) {
                    row.height = rowFmt.height;
                }
            }

            const colFmt = WjGridCustomCells.getColumnFormat(column);
            if (typeof colFmt === 'object') {
                if (colFmt.isReadOnly) {
                    cell.classList.add('readonly');
                }
                if (colFmt.cssClass) {
                    cell.classList.add(colFmt.cssClass);
                }
            }

            if (isExcelLikeCell && !isInputElement) {
                const cellValue = item[column.binding!];

                if (cellFmt.format) {
                    cell.innerText = Globalize.format(cellValue, cellFmt.format);
                }

                if (cellFmt.align) {
                    cell.classList.toggle(CellAlignmentClasses[cellFmt.align], true);
                }

                if (cellFmt.cssClass) {
                    cell.classList.add(cellFmt.cssClass);
                }

                if (cellFmt.isReadOnly) {
                    cell.classList.add('readonly');
                }
            }
        }
    }
}

